import { FindDropsListType, sortingOrder, WellInfoAllSortDataType } from './Contants';

export type FilterWell = {
  wellNumber: number;
  plateId: number;
  dropNumber?: number;
  hit?: boolean;
};

export const getWellKey = (well: { plateId: number; wellNumber: number }) =>
  `${well.plateId}-${well.wellNumber}`;

export const toCombinedWellsKey = (wells: FilterWell[] | null) => {
  if (wells === null) {
    return 'null';
  }
  if (wells.length === 0) {
    return 'empty';
  }
  return wells
    .map((well) => `${getWellKey(well)}-${well.dropNumber ?? ''}`)
    .sort()
    .join('|');
};

/**
 * Combine ingredient and score filter wells: intersection when both active; otherwise the
 * active filter's wells. Ingredient wells are well-level (ingredients are shared across all
 * drops in a well); score wells may be drop-specific. When intersecting, filter the
 * (potentially drop-specific) score wells down to wells that also pass the ingredient filter,
 * so drop-level granularity from the score filter is preserved in the result.
 */
export const combineFilteredWells = (
  ingredientWells: FilterWell[],
  scoreWells: FilterWell[],
  isIngredientFilterActive: boolean,
  isScoreFilterActive: boolean,
): FilterWell[] | null => {
  if (!isIngredientFilterActive && !isScoreFilterActive) {
    return null;
  }
  if (!isIngredientFilterActive) {
    return scoreWells;
  }
  if (!isScoreFilterActive) {
    return ingredientWells;
  }

  const ingredientWellKeys = new Set(ingredientWells.map(getWellKey));
  return scoreWells.filter((well) => ingredientWellKeys.has(getWellKey(well)));
};

export const handleSort = (
  sortNumber: number,
  wellCardList: FindDropsListType,
  wellInfoData: WellInfoAllSortDataType,
) => {
  const selectedValue = wellInfoData[sortNumber].selectedValue;
  let sortedData = [...wellCardList];

  if (selectedValue === 'Select') {
    return sortedData;
  }
  sortedData = PerformSort(sortNumber, sortedData, wellInfoData);
  return sortedData;
};

const SORT_KEY_INDEXES = [0, 1, 2];

const compareTuples = (a: number[], b: number[]) => {
  for (let i = 0; i < a.length; i++) {
    const difference = a[i] - b[i];
    if (difference) {
      return difference;
    }
  }
  return 0;
};

type SortValue = number | DropScoreRank | null;

const toTuple = (value: number | DropScoreRank): number[] =>
  Array.isArray(value) ? value : [value];

const compareAscending = (a: SortValue, b: SortValue) => {
  const isAMissing = a == null;
  const isBMissing = b == null;
  if (isAMissing || isBMissing) {
    return Number(isBMissing) - Number(isAMissing);
  }
  return compareTuples(toTuple(a), toTuple(b));
};

const compareSortValues = (a: SortValue, b: SortValue, isDescending: boolean) =>
  isDescending ? compareAscending(b, a) : compareAscending(a, b);

export const PerformSort = (
  sortNumber: number,
  wellCardList,
  wellInfoData: WellInfoAllSortDataType,
) =>
  wellCardList.sort((a, b) => {
    for (const keyIndex of SORT_KEY_INDEXES) {
      const { selectedValue, sortOrder } = wellInfoData[keyIndex];
      const isAscending = sortOrder === sortingOrder.ASCENDING;
      const isDescending = keyIndex === sortNumber ? isAscending : !isAscending;
      const comparison = compareSortValues(
        a[selectedValue],
        b[selectedValue],
        isDescending,
      );
      if (comparison) {
        return comparison;
      }
    }
    return 0;
  });

export type DropScore = {
  plateId: number;
  wellNumber: number;
  dropNumber: number;
  wellDropScoreGroupId: number;
  scoreBatchId: number;
  orderNumber: number;
};

export type DropScoreRank = [orderNumber: number, scoreGroupId: number];

export const getDropScoreRank = (
  scores: DropScore[],
  plateId: number,
  wellNumber: number,
  dropNumber: number | null,
): DropScoreRank | null => {
  const dropScores = scores.filter(
    (score) =>
      score.plateId === plateId &&
      score.wellNumber === wellNumber &&
      (dropNumber == null || score.dropNumber === dropNumber),
  );
  if (!dropScores.length) {
    return null;
  }
  const latestBatchId = Math.max(...dropScores.map((score) => score.scoreBatchId));
  const latestScores = dropScores.filter((score) => score.scoreBatchId === latestBatchId);
  const firstGroupId = Math.min(
    ...latestScores.map((score) => score.wellDropScoreGroupId),
  );
  const firstGroupOrderNumbers = latestScores
    .filter((score) => score.wellDropScoreGroupId === firstGroupId)
    .map((score) => score.orderNumber);
  return [Math.min(...firstGroupOrderNumbers), firstGroupId];
};

export const changeScoreSelectedItems = (
  score,
  allWellsScoresList,
  selectedScoreGroups,
  setSelectedScoreGroups,
  scoresSelection,
  lastActionPerformed,
) => {
  let newSelectedScoresList = [...selectedScoreGroups];
  if (
    newSelectedScoresList.some(
      (selectedScore) =>
        selectedScore.scoreGroupName === score.scoreGroupName &&
        selectedScore.scoreName === score.scoreName,
    )
  ) {
    newSelectedScoresList = newSelectedScoresList.filter(
      (selectedScore) => selectedScore.scoreName !== score.scoreName,
    );
  } else {
    const newScores = [...allWellsScoresList].filter(
      (newScore) =>
        newScore.wellDropScoreId === score.wellDropScoreId &&
        newScore.wellDropScoreGroupId === score.wellDropScoreGroupId,
    );
    newScores.length
      ? newScores.every((element) => newSelectedScoresList.push(element))
      : newSelectedScoresList.push(score);
  }
  scoresSelection(lastActionPerformed.scores, newSelectedScoresList, allWellsScoresList);
  setSelectedScoreGroups(newSelectedScoresList);
};
